import { zodResolver } from '@hookform/resolvers/zod';
import { Cancel } from '@mui/icons-material';
import {
  Button,
  Checkbox,
  Divider,
  Drawer,
  FormControl,
  FormControlLabel,
  FormLabel,
  Grid2,
  InputAdornment,
  MenuItem,
  TextField,
  Tooltip,
  Typography
} from '@mui/material';
import { DatePicker } from '@mui/x-date-pickers';
import { CreateOrPutStudentJobOffer, StudentJobOffer } from '@thro-types/jobmarket';

import { useEffect } from 'react';
import { Controller, useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import ReactQuill from 'react-quill';
import { useDispatch } from 'react-redux';

import { useJobTypesQuery } from 'api/hooks/jobmarket/jobtypes';
import { useStudentJobOfferingsMutation } from 'api/hooks/jobmarket/studentOffers';
import { SaveButton } from 'app/components';
import { AlertActions } from 'app/containers/Dashboard/Alert/slice';
import { translations } from 'locales/i18n';
import { useAuthContext } from 'utils/hooks/useAuthContext';

import './drawer.scss';
import { studentJobOfferValidationSchema, studentJobOfferInitalValues } from './validation';

interface Props {
  open: boolean;
  handleClose: () => void;
  studentOffering: StudentJobOffer | undefined;
}
export const StudentJobOfferDrawer = ({
  open,
  handleClose: handleCloseProp,
  studentOffering
}: Props) => {
  const dispatch = useDispatch();
  const { t } = useTranslation();

  const { user } = useAuthContext();

  const { data: jobTypes } = useJobTypesQuery();

  const { create, update } = useStudentJobOfferingsMutation();

  const { watch, handleSubmit, register, control, formState, reset } = useForm<
    Omit<CreateOrPutStudentJobOffer, 'jobTypeId'> & {
      accepted: boolean;
      jobTypeId: number | string;
    }
  >({
    defaultValues: studentJobOfferInitalValues(studentOffering, user),
    resolver: zodResolver(studentJobOfferValidationSchema(t))
  });

  const handleClose = () => {
    handleCloseProp();
    reset();
  };

  useEffect(() => {
    reset(studentJobOfferInitalValues(studentOffering, user));
  }, [studentOffering, user, reset]);

  const handleSaveClick = async values => {
    try {
      if (create.isPending || update.isPending) {
        return;
      }
      delete values.accepted;
      if (!values.phone.length) {
        delete values.phone;
      }
      if (values.id) {
        await update.mutateAsync({
          id: values.id,
          data: values
        });
        dispatch(
          AlertActions.success(t(translations.pages.studentJobOffers.alerts.updateSuccess()))
        );
        handleClose();
      } else {
        await create.mutateAsync(values);
        dispatch(
          AlertActions.success(t(translations.pages.studentJobOffers.alerts.createSuccess()))
        );
        handleClose();
      }
    } catch {
      if (values.id) {
        dispatch(AlertActions.error(t(translations.pages.studentJobOffers.alerts.updateFailed())));
      } else {
        dispatch(AlertActions.error(t(translations.pages.studentJobOffers.alerts.createFailed())));
      }
    }
  };
  return (
    <Drawer
      anchor="right"
      open={open}
      onClose={handleClose}
      slotProps={{ paper: { sx: { width: { xs: '70%', md: '35%', xl: '25%' } } } }}
    >
      <form onSubmit={handleSubmit(values => handleSaveClick(values))}>
        <Grid2
          container
          padding={2}
          flexDirection={'column'}
          gap={1}
          height={'100vh'}
          width={'100%'}
          justifyContent={'flex-end'}
        >
          <Grid2 container size={12} flexDirection={'column'}>
            <Typography variant="h5" fontWeight={'bold'}>
              {studentOffering?.id
                ? t(translations.pages.studentJobOffers.drawer.titleEdit())
                : t(translations.pages.studentJobOffers.drawer.titleCreate())}
            </Typography>
            <Divider />
          </Grid2>
          <Grid2 container spacing={2} size={12} maxHeight={'85vh'} sx={{ overflowY: 'auto' }}>
            <FormControl required fullWidth>
              <FormLabel>{t(translations.common.name())}</FormLabel>
              <TextField {...register('name')} size="small" fullWidth disabled />
            </FormControl>
            <FormControl required fullWidth>
              <FormLabel>{t(translations.common.email())}</FormLabel>
              <TextField
                {...register('email')}
                size="small"
                fullWidth
                error={!!formState.errors.email}
                helperText={formState.errors.email?.message}
              />
            </FormControl>
            <FormControl fullWidth>
              <FormLabel>{t(translations.pages.studentJobOffers.drawer.phone())}</FormLabel>
              <TextField {...register('phone')} id="phone" name="phone" size="small" fullWidth />
            </FormControl>
            <FormControl required fullWidth>
              <FormLabel>{t(translations.common.course())}</FormLabel>
              <Controller
                control={control}
                name="courseId"
                render={({ field }) => (
                  <TextField
                    select
                    {...field}
                    size="small"
                    fullWidth
                    disabled={user.courses.length === 1}
                    error={!!formState.errors.course}
                    helperText={formState.errors.course?.message}
                  >
                    {user.courses.map(course => (
                      <MenuItem key={course.id} value={course.id}>
                        {course.name}
                      </MenuItem>
                    ))}
                  </TextField>
                )}
              />
            </FormControl>
            <FormControl required fullWidth>
              <FormLabel>{t(translations.common.type())}</FormLabel>
              <Controller
                control={control}
                name="jobTypeId"
                render={({ field }) => (
                  <TextField
                    select
                    {...field}
                    size="small"
                    fullWidth
                    error={!!formState.errors.jobTypeId}
                    helperText={formState.errors.jobTypeId?.message}
                  >
                    {jobTypes.map(jobType => (
                      <MenuItem key={jobType.id} value={jobType.id}>
                        {jobType.name}
                      </MenuItem>
                    ))}
                  </TextField>
                )}
              />
            </FormControl>
            <FormControl required fullWidth>
              <FormLabel>{t(translations.pages.jobOffers.startDate())}</FormLabel>
              <Controller
                control={control}
                name="startDate"
                render={({ field }) => (
                  <DatePicker
                    {...field}
                    disablePast
                    renderInput={params => (
                      <TextField
                        {...params}
                        size="small"
                        error={!!formState.errors.startDate}
                        helperText={formState.errors.startDate?.message}
                      />
                    )}
                    inputFormat="dd.MM.yyyy"
                  />
                )}
              />
            </FormControl>
            <FormControl required fullWidth>
              <FormLabel>{t(translations.common.title())}</FormLabel>
              <TextField
                {...register('title')}
                size="small"
                fullWidth
                error={!!formState.errors.title}
                helperText={formState.errors.title?.message}
                slotProps={{
                  htmlInput: { maxLength: 100 },
                  input: {
                    endAdornment: (
                      <InputAdornment position="start">
                        <Tooltip
                          title={t(translations.pages.studentJobOffers.tooltips.charsLeft())}
                        >
                          <Typography variant="body2">{`${
                            100 - watch('title').length
                          }`}</Typography>
                        </Tooltip>
                      </InputAdornment>
                    )
                  }
                }}
              />
            </FormControl>
            <FormControl required fullWidth>
              <FormLabel>{t(translations.common.description())}</FormLabel>
              <Controller
                control={control}
                name="description"
                render={({ field }) => (
                  <ReactQuill
                    style={{
                      border: formState.errors.description ? '1px solid red' : '',
                      borderRadius: formState.errors.description ? '3px' : ''
                    }}
                    {...field}
                  />
                )}
              />
              {formState.errors.description && (
                <Typography
                  style={{ marginLeft: '14px' }}
                  fontSize="small"
                  color="error"
                  variant="body2"
                >
                  {formState.errors.description?.message}
                </Typography>
              )}
            </FormControl>
            <Grid2 container gap={1} size={12}>
              <Controller
                control={control}
                name="accepted"
                render={({ field }) => (
                  <FormControlLabel
                    control={<Checkbox />}
                    label={t(translations.pages.studentJobOffers.drawer.approveDataPrivacy())}
                    checked={watch('accepted')}
                    value={watch('accepted')}
                    onChange={(e, checked) => field.onChange(checked)}
                    slotProps={{ typography: { variant: 'body2' } }}
                  />
                )}
              />
            </Grid2>
          </Grid2>
          <Grid2 size={12} alignContent={'center'} marginTop={'auto'}>
            <Divider sx={{ marginLeft: 1, marginRight: 0.6, flex: 1 }} />
          </Grid2>
          <Grid2 container justifyContent={'flex-end'} gap={2} alignItems={'flex-end'}>
            <Button
              variant="contained"
              color="error"
              startIcon={<Cancel />}
              onClick={handleClose}
              disabled={create.isPending || update.isPending}
            >
              {t(translations.common.cancel())}
            </Button>
            <SaveButton
              isSubmitting={create.isPending || update.isPending}
              isValid={watch('accepted')}
            />
          </Grid2>
        </Grid2>
      </form>
    </Drawer>
  );
};
